'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  Cart,
  Cliente,
  adicionarAoCarrinho,
  alterarQuantidade,
  buscarConta,
  logoutConta,
  removerDoCarrinho,
  verCarrinho,
} from '../lib/store-api';

const EMPTY_CART: Cart = { itens: [], subtotal: 0, frete: 0, total: 0 };

interface CartContextValue {
  cart: Cart;
  count: number;
  loading: boolean;
  refresh: () => Promise<void>;
  addItem: (produtoId: number, quantidade?: number) => Promise<void>;
  updateItem: (itemId: number, quantidade: number) => Promise<void>;
  removeItem: (itemId: number) => Promise<void>;
  cliente: Cliente | null;
  clienteLoading: boolean;
  refreshCliente: () => Promise<void>;
  logout: () => Promise<void>;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<Cart>(EMPTY_CART);
  const [loading, setLoading] = useState(true);
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [clienteLoading, setClienteLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { carrinho } = await verCarrinho();
      setCart(carrinho);
    } catch {
      // Sessao ainda sem carrinho: mantem o vazio, sem quebrar a pagina.
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshCliente = useCallback(async () => {
    try {
      const { cliente } = await buscarConta();
      setCliente(cliente);
    } catch {
      setCliente(null);
    } finally {
      setClienteLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await logoutConta();
    setCliente(null);
    await refresh();
  }, [refresh]);

  useEffect(() => {
    refresh();
    refreshCliente();
  }, [refresh, refreshCliente]);

  const addItem = useCallback(async (produtoId: number, quantidade = 1) => {
    const { carrinho } = await adicionarAoCarrinho(produtoId, quantidade);
    setCart(carrinho);
  }, []);

  const updateItem = useCallback(async (itemId: number, quantidade: number) => {
    const { carrinho } = await alterarQuantidade(itemId, quantidade);
    setCart(carrinho);
  }, []);

  const removeItem = useCallback(async (itemId: number) => {
    const { carrinho } = await removerDoCarrinho(itemId);
    setCart(carrinho);
  }, []);

  const count = cart.itens.reduce((sum, item) => sum + item.quantidade, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        count,
        loading,
        refresh,
        addItem,
        updateItem,
        removeItem,
        cliente,
        clienteLoading,
        refreshCliente,
        logout,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart precisa estar dentro de um CartProvider.');
  }
  return ctx;
}
